<template>
  <section :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="articles__tabs" v-if="tabs.length > 0">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="articles__tab"
          :class="{ 'is-active': activeTab === tab.key }"
          type="button"
          @click="changeTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="articles__grid" v-if="paginatedArticles.length > 0">
        <article v-for="item in paginatedArticles" :key="item.id || item.url" class="articles-card">
          <a
            class="articles-card__media-link"
            :href="getItemHref(item)"
            :aria-label="getTitleText(item)"
            target="_block"
          >
            <AdaptiveImage
              class="articles-card__image"
              :field="{ fields: item?.fields?.articleImage?.value }"
              :is-background="false"
            />
          </a>
          <div class="articles-card__content">
            <JssRichText
              class="articles-card__title"
              :field="item?.fields?.articleTitle"
              tag="h3"
            />
            <JssRichText class="articles-card__summary" :field="item?.fields?.articleSubtitle" />
            <a class="articles-card__link" :href="getItemHref(item)" target="_block">
              {{
                item?.fields?.articleButton?.value?.text?.value ||
                fields?.learnMoreText?.value ||
                'Learn More'
              }}
            </a>
          </div>
        </article>
      </div>

      <p class="articles__empty" v-else-if="!isLoading">
        {{ fields?.emptyText?.value || 'No articles found.' }}
      </p>

      <div class="articles__loading" v-if="isLoading">Loading...</div>

      <Pagination
        v-model:currentPage="currentPage"
        :totalPages="totalPages"
        v-if="totalPages > 1"
      />
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import api from '@/api';
import utils from '@/utils';
import type { ArticlesProps } from '@/types/components/Articles';

const props = defineProps<ArticlesProps>();

const activeTab = ref('all');
const currentPage = ref<number>(1);
const isLoading = ref(false);
const articles = ref<any[]>([]);
const totalCount = ref(0);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'articles'));
const rootStyle = computed(() => utils.overall.baseComStyle(props.fields));

const pageSize = computed(() => {
  const value = props.fields?.pageSize?.value;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 6;
});

const normalizeKey = (value?: string) => {
  return value?.toLowerCase()?.replace(/\s+/g, '') || '';
};

const tabs = computed(() => {
  const configuredTabs = props.fields?.tabs || [];
  const items = configuredTabs.map((tab) => ({
    label: tab.fields?.tabName?.value || '',
    key: normalizeKey(tab.fields?.tabKey?.value || tab.fields?.tabName?.value),
  }));

  return items;
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalCount.value / pageSize.value));
});

const paginatedArticles = computed(() => articles.value);

const loadArticles = async () => {
  isLoading.value = true;
  try {
    const [res, ex] = await api.articles.getArticles({
      page: currentPage.value,
      size: pageSize.value,
      tag: activeTab.value === 'all' ? '' : activeTab.value,
    });
    const data = res || ex;
    articles.value = data?.list || [];
    totalCount.value = data?.total || articles.value.length;
  } catch (error) {
    articles.value = [];
    totalCount.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const changeTab = (tab: string) => {
  const nextTab = tab || 'all';
  if (activeTab.value === nextTab) return;

  activeTab.value = nextTab;
  if (currentPage.value === 1) {
    loadArticles();
    return;
  }
  currentPage.value = 1;
};

const getItemHref = (item: any) => {
  return item?.fields.url?.value?.url || '#';
};

const getTitleText = (item: any) => {
  return item?.fields?.articleTitle?.value?.replace(/<[^>]+>/g, '') || 'Article detail';
};

watch(totalPages, (value) => {
  if (currentPage.value > value) currentPage.value = value;
});

watch(currentPage, loadArticles);

onMounted(loadArticles);
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.articles {
  position: relative;
  width: 100%;
  padding: 32px 0 48px;
  background: $white;

  &__tabs {
    display: flex;
    gap: 32px;
    overflow-x: auto;
    border-bottom: 1px solid #e7e7e7;
    margin: 0 -16px 32px;
    padding: 0 16px;
  }

  &__tab {
    position: relative;
    flex: 0 0 auto;
    padding: 16px 0 18px;
    color: #525252;
    background: transparent;
    border: 0;
    font-size: 16px;
    line-height: 1.4;
    font-weight: 600;
    cursor: pointer;

    &.is-active {
      color: #739b78;

      &::after {
        content: '';
        position: absolute;
        right: 0;
        bottom: -1px;
        left: 0;
        height: 2px;
        background: #739b78;
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
  }

  &__empty,
  &__loading {
    margin: 48px 0 0;
    color: #656565;
    font-size: 14px;
    text-align: center;
  }

  &-card {
    overflow: hidden;
    border-radius: 8px;
    background: #faf9f7;
    padding: 20px;
  }

  &-card__media-link {
    display: block;
    overflow: hidden;
    border-radius: 8px;
    aspect-ratio: 16 / 10;
    background: #eef2ef;
  }

  &-card__image,
  &-card__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &-card__content {
    padding-top: 16px;
  }

  &-card__title {
    margin: 0;
    color: #404040;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.35;
  }

  &-card__summary {
    margin-top: 14px;
    color: #777777;
    font-size: 14px;
    line-height: 1.5;
  }

  &-card__link {
    display: inline-flex;
    margin-top: 22px;
    color: #739b78;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    text-decoration: underline;
    text-underline-offset: 3px;

    &:hover,
    &:focus-visible {
      color: #4f7454;
      outline: none;
    }
  }

  @include tablet {
    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @include tablet-landscape {
    padding: 40px 0 72px;

    &__tabs {
      gap: 48px;
      margin: 0 calc((100vw - 100%) / -2) 40px;
      padding-left: calc((100vw - 100%) / 2);
      padding-right: calc((100vw - 100%) / 2);
    }

    &__grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;
    }

    &-card {
      padding: 24px;
    }

    &-card__title {
      font-size: 18px;
    }
  }
}
</style>
